/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import { Modal, Button } from 'react-bootstrap'
import AddOrder from './AddOrder'
import ReviewOrder from './ReviewOrder'
import { addLabOrder } from '@store/actions/lab.action'
import { LabOrder } from '@dataTypes/lab.types'
import './AddLabOrder.css'
import { useAppDispatch } from '@store/hooks'

interface AddLabOrderProps {
  show: boolean
  onClose: () => void
}

const AddLabOrderModal: React.FC<AddLabOrderProps> = ({ show, onClose }) => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState(1)
  const [orderData, setOrderData] = useState<any>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [showPrescriptionNote, setShowPrescriptionNote] =
    useState<boolean>(false)
  const [tempFormData, setTempFormData] = useState<any>({
    patientName: '',
    labWorkType: '',
    providerName: '',
    partnerLabName: '',
    dueDate: '',
    priority: 'Normal',
    prescriptionDetails: [],
    attachments: [],
  })

  useEffect(() => {
    if (step === 2) {
      setOrderData(tempFormData)
    }
  }, [step, tempFormData])

  const handleNext = (): void => {
    if (!tempFormData?.patientName) {
      setShowWarning(true)
      return
    }
    setStep(2)
  }

  const handleBack = (): void => {
    setStep(1)
  }

  const resetForm = (): void => {
    setStep(1)
    setOrderData(null)
    setTempFormData({
      patientName: '',
      labWorkType: '',
      providerName: '',
      partnerLabName: '',
      dueDate: '',
      priority: 'Normal',
      prescriptionDetails: [],
      attachments: [],
    })
  }

  const generateOrderNo = (): string => {
    const randomSixDigits = Math.floor(100000 + Math.random() * 900000)
    return `LAB-${randomSixDigits}`
  }

  const handleSubmit = (): void => {
    if (!orderData) return

    const todayDate = new Date().toISOString().split('T')[0]

    const newLabOrder: LabOrder = {
      orderNo: generateOrderNo(),
      requestedOn: todayDate,
      patientName: orderData.patientName || 'Unknown',
      labWorkType: orderData.labWorkType || 'General',
      providerName: orderData.providerName || 'Unknown',
      partnerLabName: orderData.partnerLabName || 'Unknown',
      dueDate: orderData.dueDate || todayDate,
      priority: orderData.priority || 'Normal',
      totalAmount: 0,
      description: orderData.prescriptionDetails?.[0]?.procedureType || '',
      completionDate: orderData.dueDate || todayDate,
      status: 'Pending',
      prescriptions: orderData.prescriptionDetails || [],
      attachments: (orderData.attachments || []).map((att: any) => ({
        fileName: typeof att.file === 'string' ? att.file : att.file.name,
        fileType:
          typeof att.file === 'string'
            ? att.file.split('.').pop() || 'unknown'
            : att.file.name.split('.').pop() || 'unknown',
        dateTaken: att.date,
      })),
    }

    dispatch(addLabOrder(newLabOrder))
    onClose()
    resetForm()
  }

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
        <div style={{ position: 'relative' }}>
          {(showPrescriptionNote || showWarning) && (
            <div className="modal-dark-overlay"></div>
          )}

          <Modal.Header closeButton>
            <Modal.Title>
              {step === 1 ? 'Add order' : 'Review order'}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {step === 1 ? (
              <AddOrder
                formData={tempFormData}
                onChangeForm={(data) => setTempFormData(data)}
                onToggleNoteModal={setShowPrescriptionNote}
              />
            ) : orderData ? (
              <ReviewOrder data={orderData} onBack={handleBack} />
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            {step === 1 ? (
              <>
                <Button
                  variant="secondary"
                  className="button-md"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="button-md"
                  onClick={handleNext}
                >
                  Review
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  className="button-md"
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  className="button-md"
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </>
            )}
          </Modal.Footer>
        </div>
      </Modal>

      <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Incomplete form</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please complete the form before reviewing!</Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className="button-md"
            onClick={() => setShowWarning(false)}
          >
            Okay
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AddLabOrderModal
